import React, { createContext, useContext, useState, useEffect } from 'react';
import { login as apiLogin, logout as apiLogout } from '../api/client';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user,            setUser]            = useState(null);
  const [loading,         setLoading]         = useState(true);
  const [activeAccountId, setActiveAccountId] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('dx_token');
    const saved = localStorage.getItem('dx_user');
    const savedAccount = localStorage.getItem('dx_active_account');
    if (token && saved) {
      try {
        const parsed = JSON.parse(saved);
        setUser(parsed);
        setActiveAccountId(savedAccount || parsed.account_id);
      } catch {
        localStorage.removeItem('dx_user');
      }
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    const { data } = await apiLogin(email, password);
    localStorage.setItem('dx_token', data.token);
    localStorage.setItem('dx_user',  JSON.stringify(data.user));
    localStorage.removeItem('dx_active_account');
    setUser(data.user);
    setActiveAccountId(data.user.account_id);
    return data.user;
  };

  const logout = async () => {
    try { await apiLogout(); } catch {}
    localStorage.removeItem('dx_token');
    localStorage.removeItem('dx_user');
    localStorage.removeItem('dx_active_account');
    setUser(null);
    setActiveAccountId(null);
  };

  // ── Switch into a sub-account (owner/platform_admin/agency_admin only)
  const switchAccount = (accountId) => {
    if (accountId === user?.account_id) {
      localStorage.removeItem('dx_active_account');
      setActiveAccountId(user.account_id);
    } else {
      localStorage.setItem('dx_active_account', accountId);
      setActiveAccountId(accountId);
    }
  };

  const exitSwitchedAccount = () => {
    localStorage.removeItem('dx_active_account');
    setActiveAccountId(user?.account_id);
  };

  const isSwitched = activeAccountId && activeAccountId !== user?.account_id;

  // ── Role helpers (new role names)
  const isOwner         = user?.role === 'owner';
  const isPlatformAdmin = user?.role === 'owner' || user?.role === 'platform_admin';
  const isAgencyAdmin   = ['owner','platform_admin','agency_admin'].includes(user?.role);
  const isAccountAdmin  = ['owner','platform_admin','agency_admin','account_admin'].includes(user?.role);

  // Legacy aliases — keep so existing components don't break immediately
  const isSuperAdmin = isOwner;
  const isAdmin      = isPlatformAdmin;

  return (
    <AuthContext.Provider value={{
      user, loading,
      login, logout,
      activeAccountId, switchAccount, exitSwitchedAccount, isSwitched,
      isOwner, isPlatformAdmin, isAgencyAdmin, isAccountAdmin,
      isSuperAdmin, isAdmin,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
